import { useMutation } from '@apollo/client';
import React from 'react';
import Button from '../components/Button';
import Form, { Field, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import { UIActions } from '../ui/uiSlice';
import { useRouter } from '../utils/router/v3Bridge';
import groupPath from '../utils/url/groupPath';
import { GroupPolicyAction, useGroupPolicyCheck } from './groupPolicyCheck';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';

const CREATE_GROUP_MUTATION = gql(`
  mutation GroupModalCreate($name: String!) {
    group: createGroup(name: $name) {
      ...groupPath_group
    }
  }
`);

const REQUEST_GROUP_MUTATION = gql(`
  mutation GroupModalRequest($name: String!) {
    requestGroupCreation(name: $name)
  }
`);

interface GroupModalProps {
  close: () => void;
}

type Values = {
  name: string;
};

const GroupModal = ({ close }: GroupModalProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const canCreate = useGroupPolicyCheck(GroupPolicyAction.CreateGroup);

  const initialValues = {
    name: '',
  };

  const [createGroupMutation] = useMutation(CREATE_GROUP_MUTATION);
  const [requestGroupMutation] = useMutation(REQUEST_GROUP_MUTATION);

  const handleSubmit = async (values: Values) => {
    if (canCreate) {
      const { data } = await createGroupMutation({
        variables: values,
      });

      const path = data?.group && groupPath(data.group);
      if (path) {
        router.push({
          pathname: path,
          state: {
            edit: true,
          },
        });
      }
    } else {
      await requestGroupMutation({ variables: values });
      dispatch(
        UIActions.pushToast(
          {
            level: 'success',
            message: 'Your group has been requested to the admins',
          },
          3000,
        ),
      );
    }

    close();
  };

  return (
    <Modal variant="small">
      <ModalTitle small close={close}>
        {canCreate ? 'Create new group' : 'Request new group'}
      </ModalTitle>
      <Form<Values>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ isValid }) => (
          <>
            <Field
              autoFocus
              label="Name"
              name="name"
              required
              type="text"
              autoComplete="off"
            />
            <ModalActionFooter
              align="end"
              secondary={
                <Button onClick={close} variant="secondary">
                  Cancel
                </Button>
              }
              primary={
                <Submit disabled={!isValid}>
                  {canCreate ? 'Create' : 'Request'}
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default GroupModal;
